"use client"

import { useEffect, useRef } from "react"
import {
  Camera,
  Mesh,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  Vector2,
  WebGLRenderer,
} from "three"

type ShaderAnimationProps = {
  speed?: number // animation speed multiplier
  intensity?: number // brightness multiplier for lines
  lineWidth?: number // base line thickness in the shader
}

type ShaderUniform<TValue> = {
  type?: string
  value: TValue
}

type ShaderUniforms = {
  time: ShaderUniform<number>
  resolution: ShaderUniform<Vector2>
  uLineWidth: ShaderUniform<number>
  uIntensity: ShaderUniform<number>
}

type SceneRef = {
  camera: Camera
  scene: Scene
  renderer: WebGLRenderer
  uniforms: ShaderUniforms
  animationId: number
}

export function ShaderAnimation({ speed = 1.6, intensity = 1.25, lineWidth = 0.003 }: ShaderAnimationProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<SceneRef | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    const fallbackBackground =
      "radial-gradient(circle at 30% 30%, rgba(16,185,129,0.12), transparent 40%), radial-gradient(circle at 70% 40%, rgba(79,70,229,0.1), transparent 45%), #05070d"

    const prefersReducedMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const isNarrowViewport = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches

    const hasWebglSupport = (() => {
      try {
        const canvas = document.createElement("canvas")
        return Boolean(canvas.getContext("webgl", { failIfMajorPerformanceCaveat: true }) || canvas.getContext("experimental-webgl"))
      } catch {
        return false
      }
    })()

    if (!hasWebglSupport || prefersReducedMotion || isNarrowViewport) {
      container.style.background = fallbackBackground
      return
    }

    try {
      const vertexShader = `
        void main() {
          gl_Position = vec4( position, 1.0 );
        }
      `

      const fragmentShader = `
        #define TWO_PI 6.2831853072
        #define PI 3.14159265359

        precision highp float;
        uniform vec2 resolution;
        uniform float time;
        uniform float uLineWidth;
        uniform float uIntensity;

        void main(void) {
          vec2 uv = (gl_FragCoord.xy * 2.0 - resolution.xy) / min(resolution.x, resolution.y);
          float t = time*0.05;
          float lineWidth = uLineWidth;

          vec3 color = vec3(0.0);
          for(int j = 0; j < 3; j++){
            for(int i=0; i < 5; i++){
              color[j] += uIntensity * lineWidth*float(i*i) / abs(fract(t - 0.01*float(j)+float(i)*0.01)*5.0 - length(uv) + mod(uv.x+uv.y, 0.2));
            }
          }

          gl_FragColor = vec4(color[0],color[1],color[2],1.0);
        }
      `

      const camera = new Camera()
      camera.position.z = 1

      const scene = new Scene()
      const geometry = new PlaneGeometry(2, 2)

      const uniforms: ShaderUniforms = {
        time: { type: "f", value: 1.0 },
        resolution: { type: "v2", value: new Vector2() },
        uLineWidth: { type: "f", value: prefersReducedMotion ? Math.max(0.0015, lineWidth * 0.75) : lineWidth },
        uIntensity: { type: "f", value: prefersReducedMotion ? Math.max(0.9, intensity * 0.8) : intensity },
      }

      const material = new ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
      })

      const mesh = new Mesh(geometry, material)
      scene.add(mesh)

      const renderer = new WebGLRenderer({ antialias: true })
      // Cap pixel ratio for perf on high-DPI while keeping quality
      const dpr = Math.min(1.75, window.devicePixelRatio || 1)
      renderer.setPixelRatio(dpr)
      renderer.domElement.style.pointerEvents = "none"

      container.appendChild(renderer.domElement)

      const onWindowResize = () => {
        const width = container.clientWidth
        const height = container.clientHeight
        renderer.setSize(width, height)
        uniforms.resolution.value.x = renderer.domElement.width
        uniforms.resolution.value.y = renderer.domElement.height
      }

      onWindowResize()
      window.addEventListener("resize", onWindowResize, false)

      const animate = () => {
        const animationId = requestAnimationFrame(animate)
        // Slightly faster baseline; honor reduced-motion preference
        const baseStep = prefersReducedMotion ? 0.02 : 0.05
        const step = baseStep * (speed > 0 ? speed : 1)
        uniforms.time.value += step
        renderer.render(scene, camera)

        if (sceneRef.current) {
          sceneRef.current.animationId = animationId
        }
      }

      sceneRef.current = {
        camera,
        scene,
        renderer,
        uniforms,
        animationId: 0,
      }

      animate()

      return () => {
        window.removeEventListener("resize", onWindowResize)

        if (sceneRef.current) {
          cancelAnimationFrame(sceneRef.current.animationId)

          if (container && sceneRef.current.renderer.domElement) {
            container.removeChild(sceneRef.current.renderer.domElement)
          }

          sceneRef.current.renderer.dispose()
          geometry.dispose()
          material.dispose()
        }
      }
    } catch {
      container.style.background = fallbackBackground
    }
  }, [intensity, lineWidth, speed])

  return (
    <div
      ref={containerRef}
      className="w-full h-screen motion-safe:opacity-100 motion-reduce:opacity-80"
      style={{
        background: "#05070d",
        overflow: "hidden",
        filter: "brightness(0.65) contrast(1.15)",
      }}
    />
  )
}
